import { Router } from '@angular/router';
import { Breadcrumb, CoinRoute, CoinRouteTitle } from '@coin/shared/core/util-models';

export type ActiveRoute = { config: CoinRoute; url: string };

/** Active primary route chain from the root to the deepest child, each with its own config and full URL path. */
export function getActiveRouteChain(router: Router): ActiveRoute[] {
  const chain: ActiveRoute[] = [];
  for (let snapshot = router.routerState.snapshot.root; snapshot; snapshot = snapshot.firstChild) {
    if (snapshot.routeConfig) {
      const segments = snapshot.pathFromRoot.flatMap(route => route.url.map(segment => segment.path));
      chain.push({ config: snapshot.routeConfig, url: `/${segments.join('/')}` });
    }
  }
  return chain;
}

export function findNavigationRoot(chain: ActiveRoute[]): ActiveRoute | undefined {
  return [...chain].reverse().find(route => route.config.data?.navigationRoot);
}

export function joinRoutePath(base: string, path: string | undefined): string {
  if (!path) return base;
  return base.endsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

export function resolveRouteTitle(title: CoinRouteTitle | undefined, url: URL): string | undefined {
  if (!title) return undefined;
  if (typeof title === 'string') return title;
  const [param] = Object.keys(title);
  return title[param][url.searchParams.get(param) ?? ''];
}

/** Children as nav items and tabs see them: data-less component-less `''` wrappers are flattened in. */
export function getNavigationChildren(config: CoinRoute, chain: ActiveRoute[]): CoinRoute[] {
  return getOwnChildren(config, chain).flatMap(child =>
    child.path === '' &&
    !child.data &&
    !child.component &&
    !child.loadComponent &&
    (child.children || child.loadChildren)
      ? getNavigationChildren(child, chain)
      : [child]
  );
}

/** The titled `''` child, which stands in for its parent in nav titles, tabs and breadcrumbs. */
export function findTitledRootChild(config: CoinRoute, chain: ActiveRoute[]): CoinRoute | undefined {
  return getNavigationChildren(config, chain).find(
    child => child.path === '' && child.redirectTo === undefined && !!child.data?.title
  );
}

export function getBreadcrumbs(chain: ActiveRoute[], url: URL): Breadcrumb[] {
  const replacedChildren = new Set<CoinRoute>();
  const breadcrumbs: Breadcrumb[] = [];
  for (const { config, url: path } of chain) {
    if (!config.data || replacedChildren.has(config)) continue;

    const rootChild = findTitledRootChild(config, chain);
    if (rootChild) replacedChildren.add(rootChild);

    const { data } = rootChild ?? config;
    if ((!data.title && !data.paramTitle) || data.hideBreadcrumb) continue;

    breadcrumbs.push({
      url: path,
      title: resolveRouteTitle(data.title, url),
      paramTitle: data.paramTitle,
      params: data.params,
      emptyPage: data.emptyPage
    });
  }
  return breadcrumbs;
}

// Lazy-loaded children aren't readable from the config, only the one in the active chain is known.
function getOwnChildren(config: CoinRoute, chain: ActiveRoute[]): CoinRoute[] {
  if (config.children) return config.children;
  if (!config.loadChildren) return [];

  const index = chain.findIndex(route => route.config === config);
  const loadedChild = index < 0 ? undefined : chain[index + 1];
  return loadedChild ? [loadedChild.config] : [];
}
